<script>
  import { untrack } from 'svelte'
  import { Button } from '$lib/components/ui/button'
  import { AlertTriangle } from '@lucide/svelte'
  import { navigationManager } from '$lib/navigation/navigation-manager.js'
  import { retain_route, should_preserve_route } from '$lib/navigation/route-preservation.js'

  let { Component, params, path = '' } = $props()

  let preserved_pages = $state.raw([])
  let is_preserved = $derived(should_preserve_route(path))

  $effect.pre(() => {
    if (!Component || !should_preserve_route(path)) return

    const pages = untrack(() => preserved_pages)
    const existing = pages.find((page) => page.key === path)
    preserved_pages = retain_route(pages, {
      ...existing,
      key: path,
      component: Component,
      params,
    })
  })

  function report(error, page_path) {
    console.error('[RouteOutlet]', page_path, error)
    return String(error?.message || error || 'Unknown error')
  }
</script>

{#snippet route_page(Page, page_params, page_path)}
  <svelte:boundary>
    <Page params={page_params} />

    {#snippet failed(error, reset)}
      <div class="min-h-[60vh] flex items-center justify-center p-6">
        <div class="max-w-md w-full text-center space-y-5">
          <div class="mx-auto h-12 w-12 rounded-2xl bg-destructive/10 flex items-center justify-center">
            <AlertTriangle class="h-6 w-6 text-destructive" />
          </div>
          <div class="space-y-2">
            <h2 class="text-xl font-semibold tracking-tight text-foreground">Something went wrong</h2>
            <p class="text-sm text-muted-foreground">
              This page hit an unexpected error. You can retry, or head back and try again.
            </p>
            <p class="text-xs font-mono text-muted-foreground/80 break-words">{report(error, page_path)}</p>
            {#if import.meta.env.DEV}
              <pre class="mt-3 text-left text-xs text-muted-foreground bg-muted p-3 rounded-lg overflow-auto max-h-40">{String(error?.stack || error)}</pre>
            {/if}
          </div>
          <div class="flex items-center justify-center gap-2">
            <Button variant="outline" onclick={reset}>Retry</Button>
            <Button variant="ghost" onclick={() => navigationManager.back('/app')}>Go back</Button>
          </div>
        </div>
      </div>
    {/snippet}
  </svelte:boundary>
{/snippet}

{#if Component}
  {#each preserved_pages as page (page.key)}
    <div data-route-page hidden={page.key !== path} inert={page.key !== path}>
      {@render route_page(page.component, page.params, page.key)}
    </div>
  {/each}

  {#if !is_preserved}
    <div data-route-page>
      {#key path}
        {@render route_page(Component, params, path)}
      {/key}
    </div>
  {/if}
{/if}

<style>
  [data-route-page]:not([hidden]) {
    display: contents;
  }
</style>
